<template>
  <div class="admin-resume-show p-4 sm:p-6 lg:p-8">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
      <div>
        <h1 class="text-3xl font-bold text-gray-900">{{ resume.title }}</h1>
        <p class="text-gray-600 mt-1">Resume details and analytics</p>
      </div>
      <div class="flex items-center gap-2">
        <Link
          :href="route('admin.resumes.index')"
          class="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
        >
          Back to List
        </Link>
        <a
          v-if="resume.is_public"
          :href="route('resume.view', resume.slug)"
          target="_blank"
          class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
        >
          View Live
          <icon name="arrow-right" class="w-4 h-4" />
        </a>
        <button
          @click="deleteResume"
          class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Delete
        </button>
      </div>
    </div>

    <!-- Resume Information -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <!-- Main Details -->
      <div class="lg:col-span-2 space-y-6">
        <div class="bg-white rounded-lg shadow-md border border-gray-200 p-6">
          <h2 class="text-xl font-bold text-gray-900 mb-4">Resume Information</h2>
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <p class="text-gray-900">{{ resume.title }}</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Slug</label>
              <code class="px-3 py-2 bg-gray-100 rounded text-sm">{{ resume.slug }}</code>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <span
                :class="[
                  'inline-block px-3 py-1 text-sm font-medium rounded-full',
                  resume.is_public
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-800'
                ]"
              >
                {{ resume.is_public ? 'Public' : 'Private' }}
              </span>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Theme Color</label>
              <div class="flex items-center gap-2">
                <div
                  :style="{ backgroundColor: resume.theme_color }"
                  class="w-8 h-8 rounded border border-gray-300"
                ></div>
                <code class="text-sm">{{ resume.theme_color }}</code>
              </div>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Font Family</label>
              <p class="text-gray-900">{{ resume.font_family }}</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Layout</label>
              <p class="text-gray-900">{{ resume.layout }}</p>
            </div>
          </div>
        </div>

        <div v-if="resume.sections && resume.sections.length > 0" class="bg-white rounded-lg shadow-md border border-gray-200 p-6">
          <h2 class="text-xl font-bold text-gray-900 mb-4">Sections</h2>
          <div class="space-y-3">
            <div
              v-for="section in sortedSections"
              :key="section.id"
              class="p-4 bg-gray-50 rounded-lg border border-gray-200"
            >
              <div class="flex items-center justify-between">
                <div>
                  <span class="text-sm font-medium text-gray-700 capitalize">{{ section.type }}</span>
                  <span class="text-xs text-gray-500 ml-2">Order: {{ section.order }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Sidebar -->
      <div class="space-y-6">
        <!-- Analytics -->
        <div class="bg-white rounded-lg shadow-md border border-gray-200 p-6">
          <h2 class="text-xl font-bold text-gray-900 mb-4">Analytics</h2>
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Total Views</label>
              <p class="text-2xl font-bold text-green-600">{{ resume.views || 0 }}</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Sections</label>
              <p class="text-2xl font-bold text-blue-600">{{ resume.sections ? resume.sections.length : 0 }}</p>
            </div>
          </div>
        </div>

        <!-- User Information -->
        <div class="bg-white rounded-lg shadow-md border border-gray-200 p-6">
          <h2 class="text-xl font-bold text-gray-900 mb-4">Resume Owner</h2>
          <div class="space-y-2" v-if="resume.user">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <p class="text-gray-900">{{ resume.user.name }}</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <a :href="'mailto:' + resume.user.email" class="text-green-600 hover:text-green-800 hover:underline">
                {{ resume.user.email }}
              </a>
            </div>
          </div>
          <p v-else class="text-gray-500 text-sm">Guest user</p>
        </div>

        <!-- Dates -->
        <div class="bg-white rounded-lg shadow-md border border-gray-200 p-6">
          <h2 class="text-xl font-bold text-gray-900 mb-4">Dates</h2>
          <div class="space-y-2">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Created</label>
              <p class="text-gray-900 text-sm">{{ resume.created_at }}</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Last Updated</label>
              <p class="text-gray-900 text-sm">{{ resume.updated_at }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Link, router } from '@inertiajs/vue3'
import Icon from '@/Shared/Icon.vue'
import Layout from '@/Shared/Layout'

export default {
  layout: Layout,
  components: {
    Link,
    Icon,
  },
  props: {
    resume: {
      type: Object,
      required: true,
    },
    title: {
      type: String,
      default: 'Resume Details',
    },
  },
  computed: {
    sortedSections() {
      if (!this.resume.sections) return []
      return [...this.resume.sections].sort((a, b) => (a.order || 0) - (b.order || 0))
    },
  },
  methods: {
    deleteResume() {
      if (confirm('Are you sure you want to delete this resume? This action cannot be undone.')) {
        router.delete(route('admin.resumes.destroy', this.resume.id), {
          onSuccess: () => {
            router.visit(route('admin.resumes.index'))
            if (window.$toast) {
              window.$toast.success('Resume deleted successfully')
            }
          },
        })
      }
    },
  },
}
</script>

<style scoped>
.admin-resume-show {
  min-height: 100vh;
  background-color: #f9fafb;
}
</style>



